// src/pages/Payments/PaymentDetails.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { usePayment } from '../../hooks/usePayment';
import { usePayments } from '../../hooks/usePayments';
import Button from '../../components/common/Button';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const currency = (n) =>
  `RM${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PaymentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromStudent = searchParams.get('fromStudent'); // when opened from StudentDetails

  const { payment, loading, error, deletePayment } = usePayment(id);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fallback detection: user came from a /students/:id page but without ?fromStudent
  const [cameFromStudents, setCameFromStudents] = useState(false);
  useEffect(() => {
    try {
      const ref = document.referrer || '';
      const origin = window.location.origin;
      if (ref.startsWith(origin)) {
        const pathname = new URL(ref).pathname;
        if (/^\/students\/[^/]+/.test(pathname)) setCameFromStudents(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // Back target + label (context-aware)
  const backTarget = fromStudent ? `/students/${fromStudent}?tab=payments` : '/payments';
  const backLabel = fromStudent ? 'Back to Student Payments' : 'Back to Payments';

  const handleDeletePayment = async () => {
    try {
      await deletePayment();
      alert('Payment deleted successfully');
      if (fromStudent) {
        navigate(`/students/${fromStudent}?tab=payments`);
      } else if (cameFromStudents) {
        navigate(-1);
      } else {
        navigate('/payments');
      }
    } catch (e) {
      alert(`Error deleting payment: ${e.message}`);
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Refunded':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentTypeBadgeClass = (type) =>
    type === 'Full' ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800';

  // === Other Charges + Totals (new) ===
  const { useOtherCharges, usePaymentTotals } = usePayments();
  const {
    data: otherCharges = [],
    isLoading: ocLoading,
    error: ocError,
  } = useOtherCharges(id);

  const { data: totals } = usePaymentTotals(id);

  // Fallback sums when totals view is absent
  const enrollmentsTotal = useMemo(() => {
    if (totals?.enrollments_total != null) return Number(totals.enrollments_total);
    if (!payment?.enrollments) return 0;
    return payment.enrollments.reduce((s, e) => s + Number(e.amount_applied || 0), 0);
  }, [totals, payment]);

  const otherChargesTotal = useMemo(() => {
    if (totals?.other_charges_total != null) return Number(totals.other_charges_total);
    return (otherCharges || []).reduce((s, oc) => s + Number(oc.amount || 0), 0);
  }, [totals, otherCharges]);

  const itemsSubtotal = useMemo(() => {
    if (totals?.items_subtotal != null) return Number(totals.items_subtotal);
    return Math.round((enrollmentsTotal + otherChargesTotal) * 100) / 100;
  }, [totals, enrollmentsTotal, otherChargesTotal]);

  const paymentAmount = useMemo(() => {
    if (totals?.payment_amount != null) return Number(totals.payment_amount);
    return Number(payment?.amount || 0);
  }, [totals, payment]);

  const mismatch = Math.abs(paymentAmount - itemsSubtotal) > 0.01;

  if (loading) return <div className="text-center py-8">Loading payment details...</div>;
  if (error) return <div className="text-center py-8 text-red-500">Error: {error}</div>;
  if (!payment) return <div className="text-center py-8">Payment not found</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header Row 1: Back button (outline style) + Actions */}
      <div className="flex items-center justify-between mb-2">
        <div>
          {fromStudent ? (
            <Button
              variant="outline"
              onClick={() => navigate(backTarget)}
              className="flex items-center"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              {backLabel}
            </Button>
          ) : cameFromStudents ? (
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              Back to Student
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => navigate(backTarget)}
              className="flex items-center"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              {backLabel}
            </Button>
          )}
        </div>

        <div className="flex space-x-2">
          <Link
            to={`/payments/${payment.id}/edit${fromStudent ? `?fromStudent=${fromStudent}` : ''}`}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
          >
            Edit Payment
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
          >
            Delete Payment
          </button>
        </div>
      </div>

      {/* Header Row 2: Page Title */}
      <h1 className="text-xl font-bold mb-6">Payment Details</h1>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
            <p className="mb-6">
              Remove this payment? This is a soft delete and can be restored from the database if needed.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePayment}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {(ocError) && (
        <div className="mb-4 p-3 rounded bg-red-100 text-red-700">
          {String(ocError)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Payment info + Enrollments + Other Charges */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Payment Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Invoice Number</p>
                <p className="font-medium">{payment.invoice_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Date</p>
                <p className="font-medium">
                  {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Amount</p>
                <p className="font-medium">{currency(payment.amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-medium">{payment.payment_method}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Type</p>
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentTypeBadgeClass(
                    payment.payment_type
                  )}`}
                >
                  {payment.payment_type}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                    payment.status
                  )}`}
                >
                  {payment.status}
                </span>
              </div>
            </div>

            {payment.notes && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="font-medium whitespace-pre-wrap">{payment.notes}</p>
              </div>
            )}

            {payment.receipt_url && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Receipt</p>
                <a
                  href={payment.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  View Receipt
                </a>
              </div>
            )}
          </div>

          {/* Enrollments Covered */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Enrollments Covered</h2>
            {payment.enrollments && payment.enrollments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Intake
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount Applied
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payment.enrollments.map((enrollment, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{enrollment.subject_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{enrollment.intake_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {currency(enrollment.amount_applied)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-3 text-right font-medium" colSpan={2}>
                        Enrollments Total
                      </td>
                      <td className="px-6 py-3 font-semibold">
                        {currency(enrollmentsTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No enrollments linked to this payment</p>
            )}
          </div>

          {/* Other Charges (NEW) */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Other Charges</h2>
            {ocLoading ? (
              <div className="py-4 text-gray-600">Loading other charges…</div>
            ) : otherCharges.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount (RM)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {otherCharges.map((oc) => (
                      <tr key={oc.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {oc.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {oc.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {currency(oc.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-3 text-right font-medium" colSpan={2}>
                        Other Charges Total
                      </td>
                      <td className="px-6 py-3 font-semibold">
                        {currency(otherChargesTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No other charges for this payment</p>
            )}
          </div>
        </div>

        {/* RIGHT: Student Information + Totals (NEW) */}
        <div className="space-y-6">
          {/* Student Information (unchanged) */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Student Information</h2>
            {payment.student_id ? (
              <div>
                <div className="mb-4">
                  <p className="text-sm text-gray-500">Student Name</p>
                  <p className="font-medium">{payment.student_name || 'N/A'}</p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-500">NRIC/Passport</p>
                  <p className="font-medium">{payment.student_nric_passport}</p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-500">Contact</p>
                  <p className="font-medium">{payment.student_contact_no}</p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{payment.student_email}</p>
                </div>

                {/* Hide this button when we arrived from Student Details Payments tab */}
                {!fromStudent && (
                  <Link
                    to={`/students/${payment.student_id}`}
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-center w-full"
                  >
                    View Student Details
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No student linked to this payment</p>
            )}
          </div>

          {/* Totals (NEW) */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-3">Totals</h2>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span>Enrollments total</span>
                <span>{currency(enrollmentsTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Other Charges total</span>
                <span>{currency(otherChargesTotal)}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>Items subtotal</span>
                <span>{currency(itemsSubtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Payment Amount</span>
                <span>{currency(paymentAmount)}</span>
              </div>

              {totals ? (
                <p className="mt-2 text-xs text-gray-500">
                  Totals powered by <code>v_payment_totals</code>.
                </p>
              ) : (
                <p className="mt-2 text-xs text-gray-500">
                  Totals computed in UI (create the <code>v_payment_totals</code> view for server-side reconciliation).
                </p>
              )}

              {mismatch ? (
                <div className="mt-2 p-2 rounded bg-red-50 text-red-700">
                  Payment Amount does not match items subtotal.
                </div>
              ) : (
                <div className="mt-2 p-2 rounded bg-green-50 text-green-700">
                  Payment Amount matches items subtotal.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
